import { addHours, addMinutes, addSeconds, differenceInMinutes, isBefore, subMilliseconds, subMinutes } from 'date-fns';
import { compact, filter, last, map, mapValues, uniq } from 'lodash';
import { useMemo, useRef } from 'react';
import { Simplify } from 'type-fest';

import { useSensorInfoById } from '@/api/device';
import {
    PerSensor,
    ProcessingSource,
    RawSensorData,
    SPLRawBandStats,
    SoundPressureBandStatsSample,
    SoundPressureQuietSample,
    SoundPressureReading,
    SoundPressureSample,
    SoundPressureSampleStatsDatum,
} from '@/api/historicalData';
import { combineProcessingSources, consolidateBySensorPosition, sensorKey } from '@/api/historicalData/helpers';
import {
    SPL_BAND_SPL_READING_KEYS,
    SPL_BROADBAND_READING_KEYS,
    SPL_READING_KEYS,
    getDatumByAnyKey,
} from '@/api/historicalData/types';
import {
    UseHistoricalSensorDataOptions,
    getHistoricalSensorData,
    useRawSensorData,
} from '@/api/historicalData/useHistoricalSensorData';
import {
    CombinedQueriesResult,
    CombinedQueryOrQueriesResult,
    CombinedQueryResult,
    QueryConfig,
    SpotterIdOrIds,
    pickQueryStatus,
} from '@/common/react-query';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useStableValue } from '@/hooks/useStableValue';
import { DateRange, useGlobalStore } from '@/store';
import { ChunkHours, equalsShallow, startOfHourUTC, trimToDateRange } from '@/utils';

export const SPL_BAND_STATS_DATA_KEYS: readonly string[] = [
    'bm_borealis_spl_band_stats',
    'bm_borealis2_spl_band_stats',
];
export const SPL_BAND_STATS_HDR_DATA_KEYS: readonly string[] = [
    'bm_borealis_spl_band_stats_hdr',
    'bm_borealis2_spl_band_stats_hdr',
];
export const SPL_BROADBAND_DATA_KEYS: readonly string[] = [
    'bm_borealis_spl_broadband_8bits',
    'bm_borealis2_spl_broadband_8bits',
];
export const SPL_BROADBAND_HDR_DATA_KEYS: readonly string[] = [
    'bm_borealis_spl_broadband_hdr',
    'bm_borealis2_spl_broadband_hdr',
];
export const SPL_SAMPLE_KEYS: readonly string[] = [
    ...SPL_BAND_STATS_DATA_KEYS,
    ...SPL_BAND_STATS_HDR_DATA_KEYS,
    ...SPL_BROADBAND_DATA_KEYS,
    ...SPL_BROADBAND_HDR_DATA_KEYS,
];

const SPL_READING_BANDS = [
    { frequency: 40, bandNumber: 16 },
    { frequency: 50, bandNumber: 17 },
    { frequency: 63, bandNumber: 18 },
    { frequency: 80, bandNumber: 19 },
    { frequency: 100, bandNumber: 20 },
    { frequency: 125, bandNumber: 21 },
    { frequency: 160, bandNumber: 22 },
    { frequency: 200, bandNumber: 23 },
    { frequency: 250, bandNumber: 24 },
    { frequency: 315, bandNumber: 25 },
    { frequency: 400, bandNumber: 26 },
    { frequency: 500, bandNumber: 27 },
    { frequency: 630, bandNumber: 28 },
    { frequency: 800, bandNumber: 29 },
    { frequency: 1000, bandNumber: 30 },
    { frequency: 1250, bandNumber: 31 },
    { frequency: 1600, bandNumber: 32 },
    { frequency: 2000, bandNumber: 33 },
    { frequency: 2500, bandNumber: 34 },
    { frequency: 3150, bandNumber: 35 },
    { frequency: 4000, bandNumber: 36 },
    { frequency: 5000, bandNumber: 37 },
    { frequency: 6300, bandNumber: 38 },
    { frequency: 8000, bandNumber: 39 },
    { frequency: 10000, bandNumber: 40 },
    { frequency: 12500, bandNumber: 41 },
    { frequency: 16000, bandNumber: 42 },
    { frequency: 20000, bandNumber: 43 },
];

/**
 * The samples built from a window of raw rows, so that the sections sharing that window share them.
 *
 * Two lookups, because callers do not agree on the identity of the window itself. `useQueries` runs the
 * combined result through `replaceEqualDeep` once per observer, and that rebuilds the array whenever its
 * length changed, so each caller holds its own container over the same row objects. The rows are therefore
 * what identifies a window: the fast path is the container a caller already asked about, and the fallback
 * compares row references, which is O(rows) against building a sample and its bands for every one of them.
 */
const samplesByContainer = new WeakMap<object, Map<string, PerSensor<SoundPressureSample[]>>>();

/**
 * The window built last, and the rows and variant it was built from.
 *
 * A single slot, so that the rows held alive here are one window's rather than one window's per variant: the
 * callers sharing a window ask within the same commit, and a caller that has asked once is answered by
 * {@link samplesByContainer} from then on.
 */
let latestSamples: { variant: string; rows: RawSensorData[]; samples: PerSensor<SoundPressureSample[]> } | null = null;

/**
 * {@link buildSamples} over a window, answered from the caches above whenever that window was already built.
 *
 * The variant names every input {@link buildSamples} reads apart from the rows themselves, which is what makes
 * a hit safe to serve: `sampleIntervalsKey` stands for the configs because the sample intervals are all
 * {@link buildSamples} takes from them. Widen what it reads out of a config and the key has to name that too.
 *
 * `rawSplData` is a filter of `rawData` taken by the caller, so keying on the container the rows arrived in
 * identifies both.
 */
const buildSamplesOnce = (
    rawData: RawSensorData[],
    rawSplData: RawSensorData[],
    sensorConfigsData: SensorConfigsById | undefined,
    sampleIntervalsKey: string,
    processingSource: ProcessingSource | 'best'
): PerSensor<SoundPressureSample[]> => {
    const variant = `${processingSource}|${sampleIntervalsKey}`;

    let byVariant = samplesByContainer.get(rawData);
    if (!byVariant) {
        byVariant = new Map();
        samplesByContainer.set(rawData, byVariant);
    }

    const heldForContainer = byVariant.get(variant);
    if (heldForContainer) {
        return heldForContainer;
    }

    if (latestSamples?.variant === variant && equalsShallow(latestSamples.rows, rawData)) {
        byVariant.set(variant, latestSamples.samples);
        return latestSamples.samples;
    }

    const built = buildSamples(rawSplData, sensorConfigsData, processingSource);
    byVariant.set(variant, built);
    latestSamples = { variant, rows: rawData, samples: built };
    return built;
};

export const useSoundPressureData = (options: UseHistoricalSensorDataOptions) => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: rawData, ...response } = useRawSensorData(options);
    const { spotterId, dateRange } = options;

    const rawSplData = useMemo(() => {
        if (!rawData) return undefined;

        return filter(rawData, (d) => SPL_SAMPLE_KEYS.includes(d.data_type_name));
    }, [rawData]);

    /*
     * There is one checksum per raw row but only a handful of distinct values, so use a stable reference to
     * avoid making `useSensorInfoById` re-run when the configs don't actually change.
     */
    const sensorConfigChecksums = useStableValue(
        useMemo(() => uniq(compact(map(rawSplData ?? [], 'configuration_checksum'))), [rawSplData])
    );

    const { data: sensorConfigsData } = useSensorInfoById({
        spotterId,
        sensorConfigChecksums,
    });

    /**
     * The sample intervals are the only thing {@link buildSamples} reads out of the configs, and there are only
     * ever a handful of distinct checksums, so this is what identifies a config set for the shared cache.
     */
    const sampleIntervalsKey = useMemo(
        () =>
            Object.entries(sensorConfigsData ?? {})
                .map(
                    ([checksum, info]) => `${checksum}:${info?.sensorConfigurations.mainSensor?.sampleIntervalMinutes}`
                )
                .sort()
                .join('|'),
        [sensorConfigsData]
    );

    /*
     * Kept apart from the gap fill below so that it does not re-run when only the date range moved:
     * `DateRangeContext` advances a live `endDate` every five minutes, and this is the expensive stage.
     */
    const combinedData: PerSensor<SoundPressureSample[]> | undefined = useMemo(() => {
        if (!rawData || !rawSplData) return undefined;

        return buildSamplesOnce(rawData, rawSplData, sensorConfigsData, sampleIntervalsKey, processingSource);
    }, [rawData, rawSplData, sensorConfigsData, sampleIntervalsKey, processingSource]);

    /*
     * Only the bounds are used, and only to pad the ends, so depending on the range object itself would
     * put this back on the five minute tick for no benefit.
     */
    const startMs = dateRange?.startDate.getTime();
    const endMs = dateRange?.endDate.getTime();
    const gapFillRange = useMemo(
        () =>
            startMs === undefined || endMs === undefined
                ? null
                : { startDate: new Date(startMs), endDate: new Date(endMs) },
        [startMs, endMs]
    );

    /*
     * The charts key their draw effects on this array's identity, and a live `endDate` advancing every five
     * minutes usually lands inside the same sample interval, producing an identical timeline. Hold the
     * previous array in that case so the spectrograms are not torn down and rebuilt.
     */
    const previousData = useRef<PerSensor<SoundPressureSample[]>>();

    const data: PerSensor<SoundPressureSample[]> | undefined = useMemo(() => {
        if (!combinedData) {
            return undefined;
        }

        const filled = mapValues(combinedData, (samples, sensorPosition) => {
            const samplesFilled = fillSampleGaps(samples, gapFillRange);
            const previous = previousData.current?.[sensorPosition];
            return previous && sampleTimelinesMatch(previous, samplesFilled) ? previous : samplesFilled;
        });

        previousData.current = filled;
        return filled;
    }, [combinedData, gapFillRange]);

    return { data, ...response };
};

type SensorConfigsById = Record<
    string,
    { sensorConfigurations: { mainSensor?: { sampleIntervalMinutes?: number } } } | undefined
>;

/**
 * Folds a window of raw SPL rows into one sample per instant per sensor.
 *
 * The expensive stage: every sample carries one object per frequency band. Kept out of the hook so that
 * {@link buildSamplesOnce} is the only thing deciding whether it runs.
 */
function buildSamples(
    rawSplData: RawSensorData[],
    sensorConfigsData: SensorConfigsById | undefined,
    processingSource: ProcessingSource | 'best'
): PerSensor<SoundPressureSample[]> {
    const sensorData = consolidateBySensorPosition(rawSplData);

    const samplesPerSensor = mapValues(sensorData, (data) =>
        data.flatMap((datum) => {
            const broadbandSplHdr = getDatumByAnyKey(datum.data, SPL_BROADBAND_HDR_DATA_KEYS);
            const broadbandSpl = getDatumByAnyKey(datum.data, SPL_BROADBAND_DATA_KEYS);
            const broadbandSplDatum = (broadbandSplHdr ?? broadbandSpl)?.value;
            if (!broadbandSplDatum) {
                return [];
            }

            const commonSampleData = {
                timestamp: datum.timestamp,
                sensorPosition: sensorKey(datum.sensorPosition),
                sampleIntervalMinutes:
                    sensorConfigsData?.[datum.sensorConfigChecksum]?.sensorConfigurations.mainSensor
                        ?.sampleIntervalMinutes,
                broadbandSpl: broadbandSplDatum,
            };

            const bandStatsHdr = getDatumByAnyKey(datum.data, SPL_BAND_STATS_HDR_DATA_KEYS);
            const bandStats = getDatumByAnyKey(datum.data, SPL_BAND_STATS_DATA_KEYS);
            if (bandStatsHdr) {
                const value = bandStatsHdr.value as unknown as SPLRawBandStats;
                return {
                    dataQuality: 'present',
                    processing_source: 'hdr',
                    ...commonSampleData,
                    bandStats: bandStatsFromRaw(value),
                } satisfies SoundPressureBandStatsSample;
            } else if (bandStats) {
                const value = bandStats.value as unknown as SPLRawBandStats;
                return {
                    dataQuality: 'present',
                    processing_source: 'embedded',
                    ...commonSampleData,
                    bandStats: bandStatsFromRaw(value),
                } satisfies SoundPressureBandStatsSample;
            } else if (broadbandSplHdr) {
                return {
                    dataQuality: 'tooQuiet',
                    processing_source: 'hdr',
                    ...commonSampleData,
                } satisfies SoundPressureQuietSample;
            } else if (broadbandSpl) {
                return {
                    dataQuality: 'tooQuiet',
                    processing_source: 'embedded',
                    ...commonSampleData,
                } satisfies SoundPressureQuietSample;
            } else {
                return [];
            }
        })
    );

    return mapValues(samplesPerSensor, (samples) => {
        const lastSample = last(samples);
        if (!lastSample) {
            return [];
        }
        return combineProcessingSources(samples, lastSample.sampleIntervalMinutes ?? 30, processingSource)!;
    });
}

const SAMPLE_GAP_CLOSE_ENOUGH_MINUTES = 2;

function bandStatsFromRaw(value: SPLRawBandStats): SoundPressureSampleStatsDatum[] {
    return value.frequency.map<SoundPressureSampleStatsDatum>((freq, i) => ({
        min: value['1st_percentile']?.[i],
        lower: value['25th_percentile'][i],
        median: value['50th_percentile'][i],
        mean: value.mean[i],
        upper: value['75th_percentile'][i],
        max: value['99th_percentile']?.[i],
        frequency: freq,
        bandNumber: value.band_number[i],
    }));
}

/**
 * Yields the samples with the bookends around them, without touching the caller's array. Avoids copying
 * the entire array without mutating the input.
 */
function* withBookends<TItem>(leading: TItem | null, items: TItem[], trailing: TItem | null) {
    if (leading) {
        yield leading;
    }
    yield* items;
    if (trailing) {
        yield trailing;
    }
}

/**
 * Whether two gap-filled timelines describe the same samples at the same instants.
 *
 * `fillSampleGaps` hands the caller's own sample objects straight through and only allocates the absent
 * filler between them, so the real samples compare by reference and only the filler needs its timestamp read.
 *
 * Only the filler is ever compared by value. A real sample that is not the very same object came out of a
 * recomputed `combinedData`, and its readings can have changed under an unchanged timestamp — an HDR
 * sample landing on an instant that had only an embedded one is exactly that. Matching those by timestamp
 * would keep the superseded values on screen.
 */
function sampleTimelinesMatch(previous: SoundPressureSample[], next: SoundPressureSample[]) {
    return (
        previous.length === next.length &&
        previous.every(
            (sample, i) =>
                sample === next[i] ||
                (sample.dataQuality === 'absent' &&
                    next[i].dataQuality === 'absent' &&
                    sample.timestamp === next[i].timestamp)
        )
    );
}

function fillSampleGaps(samples: SoundPressureSample[], dateRange?: DateRange | null) {
    const result: SoundPressureSample[] = [];
    if (!samples?.length) {
        return result;
    }

    let leadingSample: SoundPressureSample | null = null;
    let bookendSample: SoundPressureSample | null = null;
    if (dateRange) {
        const firstSample = samples[0];
        if (
            firstSample.sampleIntervalMinutes &&
            differenceInMinutes(new Date(firstSample.timestamp), dateRange.startDate) >=
                firstSample.sampleIntervalMinutes
        ) {
            leadingSample = {
                dataQuality: 'absent',
                timestamp: dateRange.startDate.toISOString(),
                sampleIntervalMinutes: firstSample.sampleIntervalMinutes,
                sensorPosition: firstSample.sensorPosition,
            };
        }
        const lastSample = last(samples)!;
        // this one isn't sample interval aligned, but we use it to drive filling in the gap in the for(samples) loop below,
        // then we'll skip it when assembling the final result
        bookendSample = {
            dataQuality: 'absent',
            timestamp: dateRange.endDate.toISOString(),
            sampleIntervalMinutes: lastSample.sampleIntervalMinutes,
            sensorPosition: lastSample.sensorPosition,
        };
    }

    for (const sample of withBookends(leadingSample, samples, bookendSample)) {
        let lastResult = last(result);
        // letting there be a 2 minute gap so we don't have to worry about slight misalignments; safe because the minimum sample interval is 5 mins
        const closeEnoughTimestamp = subMinutes(new Date(sample.timestamp), SAMPLE_GAP_CLOSE_ENOUGH_MINUTES);
        if (lastResult?.sampleIntervalMinutes) {
            let lastSampleEnd: Date;
            while (
                (lastSampleEnd = addMinutes(new Date(lastResult.timestamp), lastResult.sampleIntervalMinutes!)) &&
                isBefore(lastSampleEnd, closeEnoughTimestamp)
            ) {
                const missingResult: SoundPressureSample = {
                    dataQuality: 'absent',
                    timestamp: lastSampleEnd.toISOString(),
                    sampleIntervalMinutes: lastResult.sampleIntervalMinutes,
                    sensorPosition: lastResult.sensorPosition,
                };
                result.push(missingResult);
                lastResult = missingResult;
            }
        }
        if (sample !== bookendSample) {
            result.push(sample);
        }
    }

    return result;
}

/**
 * Readings are one per second per sensor, so a window of them carries far more than the same window of any
 * other series on `/fetch/sensor-data`. The width the span would pick is wide enough for the endpoint to
 * struggle to answer, so this series names its own.
 */
const SOUND_PRESSURE_READINGS_MAX_CHUNK_HOURS: ChunkHours = 2;

export type UseSoundPressureReadingsOptions = {
    dateRange?: DateRange | null;
    /**
     * If true, missing time between readings will be filled with placeholder readings with broadbandSpl: NaN
     */
    fillGaps?: boolean;
    /**
     * Materializes the per-frequency bands on each reading. Off unless asked for: the bands are 28 objects
     * per reading per second, and only the spectrogram and the detection detail read them.
     */
    includeBands?: boolean;
    config?: Omit<QueryConfig<typeof getHistoricalSensorData>, 'select'>;
} & SpotterIdOrIds;

export function useSoundPressureReadings(
    options: Simplify<Omit<UseSoundPressureReadingsOptions, 'spotterIds'>>
): CombinedQueryResult<PerSensor<SoundPressureReading[]>>;

export function useSoundPressureReadings(
    options: Simplify<Omit<UseSoundPressureReadingsOptions, 'spotterId'>>
): CombinedQueriesResult<PerSensor<SoundPressureReading[]>>;

export function useSoundPressureReadings({
    spotterId,
    spotterIds,
    dateRange,
    fillGaps,
    includeBands = false,
    config,
}: UseSoundPressureReadingsOptions): CombinedQueryOrQueriesResult<PerSensor<SoundPressureReading[]>> {
    const isSensorHdrCachingEnabled = useFeatureFlag('trySensorHdrCache');

    const spotterIdsList = useMemo(() => {
        if (spotterId) {
            return [spotterId];
        }
        return spotterIds;
    }, [spotterId, spotterIds]);
    const startMs = dateRange?.startDate.getTime();
    const endMs = dateRange?.endDate.getTime();

    /**
     * Readings are one per second, so how much of the timeline this asks for is what decides whether the
     * endpoint can answer at all. Widened past the caller's range so that selecting a neighbouring sample in
     * the same hour is served from cache.
     *
     * Snapped on the UTC clock face for the same reason `splitLiveRange` is, so the window is the same instant
     * for every viewer whatever their timezone preference, and lines up with the live split's own boundary.
     */
    const expandedDateRange = useMemo(
        () =>
            startMs === undefined || endMs === undefined
                ? null
                : {
                      startDate: startOfHourUTC(new Date(startMs)),
                      endDate: addHours(startOfHourUTC(new Date(endMs)), 1),
                  },
        [startMs, endMs]
    );
    const { data: rawData, ...response } = useRawSensorData({
        spotterIds: spotterIdsList,
        includeSoundPressureReadings: true,
        excludeSoundPressureBandReadings: !includeBands,
        dateRange: expandedDateRange,
        useReadingsCache: isSensorHdrCachingEnabled,
        maxChunkHours: SOUND_PRESSURE_READINGS_MAX_CHUNK_HOURS,
        config,
    });

    /*
     * Trimmed to exactly the range the caller asked for. Consumers treat these readings as the whole of
     * what falls inside that range: `useSplBreachDetections` raises a detection for any breach among them,
     * and a detection outside the range is drawn past the end of an axis taken from the same range.
     */
    const windowedDataBySpotter = useMemo(() => {
        if (!rawData || !rawData.length || startMs === undefined || endMs === undefined) {
            return undefined;
        }
        return rawData.map((spotterData) => {
            const readingRows = filter(spotterData, (d) => SPL_READING_KEYS.includes(d.data_type_name));
            return trimToDateRange(readingRows, { startDate: new Date(startMs), endDate: new Date(endMs) });
        });
    }, [rawData, startMs, endMs]);

    /*
     * Stable return values for when the freshly queried data matches the previously queried data exactly.
     *
     * Keyed by spotter id: the viewport decides which spotters are asked for, so a spotter's place in the
     * list moves under it, and a hold read from the wrong place never matches.
     */
    const previous = useRef(
        new Map<string, { rows: RawSensorData[]; data: PerSensor<SoundPressureReading[]>; includeBands: boolean }>()
    );
    const previousArray = useRef<PerSensor<SoundPressureReading[]>[]>();

    const perSensorDataBySpotter: PerSensor<SoundPressureReading[]>[] | undefined = useMemo(() => {
        // the rows are the answers to the spotters that were asked for, so one names the other by position
        if (!windowedDataBySpotter || !spotterIdsList) return undefined;

        const nextPrevious = new Map<
            string,
            { rows: RawSensorData[]; data: PerSensor<SoundPressureReading[]>; includeBands: boolean }
        >();
        const next = windowedDataBySpotter.map((spotterData, spotterIdx) => {
            const heldKey = spotterIdsList[spotterIdx];
            const held = previous.current.get(heldKey);
            if (held && held.includeBands === includeBands && equalsShallow(held.rows, spotterData)) {
                nextPrevious.set(heldKey, held);
                return held.data;
            }
            const sensorData = consolidateBySensorPosition(spotterData);
            const readings = mapValues(sensorData, (data) => {
                const readings = data
                    .map((datum): SoundPressureReading | undefined => {
                        const bandSpls = getDatumByAnyKey(datum.data, SPL_BAND_SPL_READING_KEYS)?.value as unknown as
                            | number[]
                            | undefined;
                        const broadbandSpl = getDatumByAnyKey(datum.data, SPL_BROADBAND_READING_KEYS)
                            ?.value as unknown as number | undefined;
                        if (!broadbandSpl) {
                            return undefined;
                        }
                        return {
                            timestamp: datum.timestamp,
                            sensorPosition: sensorKey(datum.sensorPosition),
                            broadbandSpl: broadbandSpl,
                            bands: includeBands
                                ? (bandSpls?.map((spl, i) => ({ ...SPL_READING_BANDS[i], spl })) ?? [])
                                : [],
                        };
                    })
                    .filter((x) => x) as SoundPressureReading[];
                return fillGaps ? fillReadingGaps(readings, 1) : readings;
            });

            nextPrevious.set(heldKey, { rows: spotterData, data: readings, includeBands });
            return readings;
        });
        previous.current = nextPrevious;

        const heldArray = previousArray.current;
        if (heldArray && equalsShallow(heldArray, next)) {
            return heldArray;
        }
        previousArray.current = next;
        return next;
    }, [windowedDataBySpotter, spotterIdsList, fillGaps, includeBands]);

    const data = useMemo(() => {
        if (spotterId) {
            return perSensorDataBySpotter?.[0];
        }
        return perSensorDataBySpotter;
    }, [spotterId, perSensorDataBySpotter]);

    return { data, ...pickQueryStatus(response) };
}

const READING_GAP_CLOSE_ENOUGH_MS = 300;

function fillReadingGaps(readings: SoundPressureReading[], readingRateSeconds: number) {
    const result: SoundPressureReading[] = [];
    if (!readings?.length) {
        return result;
    }

    for (const reading of readings) {
        let lastResult = last(result);
        if (!lastResult) {
            result.push(reading);
            continue;
        }
        const closeEnoughTimestamp = subMilliseconds(new Date(reading.timestamp), READING_GAP_CLOSE_ENOUGH_MS);
        let lastReadingEnd: Date;
        while (
            (lastReadingEnd = addSeconds(new Date(lastResult!.timestamp), readingRateSeconds)) &&
            isBefore(lastReadingEnd, closeEnoughTimestamp)
        ) {
            const missingResult: SoundPressureReading = {
                timestamp: lastReadingEnd.toISOString(),
                sensorPosition: lastResult.sensorPosition,
                broadbandSpl: NaN,
                bands: [],
            };
            result.push(missingResult);
            lastResult = missingResult;
        }
        result.push(reading);
    }

    return result;
}

export const exportForTests = {
    fillSampleGaps,
    fillReadingGaps,
    resetSampleCache: () => {
        // The caches above are module state, and a stale window would answer the next test's first render
        latestSamples = null;
    },
};
